import { faGripVertical } from '@fortawesome/pro-solid-svg-icons';
import type {
	FilterConfig,
	FilterRule,
	ListBuilderConfiguration,
	ListConfiguration,
} from '@supply-mover-2-0/core';
import {
	CustomFieldQueryService,
	FilterRegistry,
	getValidFiltersForObjects,
} from '@supply-mover-2-0/core';
import type { CustomFieldEntityType } from '@supply-mover-2-0/shared';
import React, {
	forwardRef,
	useEffect,
	useImperativeHandle,
	useMemo,
	useRef,
	useState,
} from 'react';
import { Icon } from '../../../components/base/Icon';
import { Checkbox } from '../../../components/forms/Checkbox';
import { useThemeColors } from '../../../providers/ThemeProvider';
import { useTranslation } from '../../../providers/TranslationProvider';
import { cn } from '../../../utils/cn';
import { useListBuilderContextSafe } from '../core/ListBuilderProvider';
import { DragHandle } from '../shared/DraggableList';
import { DualSortableProvider, SortableZone } from '../shared/DualSortableList';
import { FilterRuleCard } from './FilterRuleCard';

export interface FiltersTabState {
	hasPendingChanges: boolean;
}

/**
 * Snapshot of the filter-tab edits that ColumnEditorLayout's saveAll will
 * serialize into the saved-search update payload. activeFilters/listOverrides
 * may be null to mean "inherit", or an array/object to mean "override".
 */
export interface FiltersTabPendingChanges {
	activeFilters: Array<string> | null;
	filterRules: Record<string, FilterRule> | null;
	listOverrides: Record<string, unknown> | null;
}

export interface FiltersTabHandle {
	/**
	 * Return the working state for the currently selected list, even if
	 * unsaved. ColumnEditorLayout calls this in its saveAll path so changes
	 * across both tabs persist in one update.
	 */
	getPendingChanges: () => FiltersTabPendingChanges | null;
	/**
	 * Reset the working state to the saved-search's persisted values. Called
	 * when the user discards changes or after a successful save.
	 */
	reset: () => void;
}

export interface FiltersTabContentProps {
	/**
	 * The merged list config (base list + saved-search overrides applied).
	 * Provides the starting point for activeFilters and filterRules edits, plus
	 * the configuration.objects we use to filter the available registry.
	 */
	listConfig: ListConfiguration;
	configuration: ListBuilderConfiguration | undefined;
	/** Raw saved-search overrides — null fields = inherit. Used to seed working state. */
	savedSearchOverrides: {
		activeFilters: Array<string> | null;
		filterRules: Record<string, FilterRule> | null;
		listOverrides: Record<string, unknown> | null;
	} | null;
	isReadOnly: boolean;
	onStateChange: (state: FiltersTabState) => void;
}

/**
 * Filters tab for the List Configuration modal. Three sections vertically:
 *   1. List behavior — showFiltersByDefault checkbox (persists to
 *      listOverrides JSON).
 *   2. Active filters — consolidated list of FilterRuleCards. Each card is
 *      a saved-search-overrideable filter with its defaultValue editor, lock,
 *      label/placeholder overrides, and Advanced (permission conditions +
 *      additionalValues).
 *   3. Available filters — pool of filters not yet active.
 *
 * Active and Available are one DualSortableProvider (dnd-kit): reorder within
 * Active, drag a filter from Available to add it, or drag a card into Available
 * to remove it.
 *
 * Read-only when the user has selected a bundled (default) list on the
 * sidebar; they need to "Save As New" / "Copy List" to customize.
 */
export const FiltersTabContent = forwardRef<
	FiltersTabHandle,
	FiltersTabContentProps
>(
	(
		{
			listConfig,
			configuration,
			savedSearchOverrides,
			isReadOnly,
			onStateChange,
		},
		ref,
	) => {
		const { t } = useTranslation();
		const colors = useThemeColors();

		// Resolved active set: saved-search override wins, otherwise inherit
		// from the merged list config (which already includes per-list activeFilters
		// or falls back to configuration-level activeFilters).
		const resolvedActiveFilters = useMemo<Array<string>>(() => {
			if (savedSearchOverrides?.activeFilters != null) {
				return savedSearchOverrides.activeFilters;
			}
			if (listConfig.activeFilters != null) {
				return listConfig.activeFilters;
			}
			return configuration?.filters?.activeFilters ?? [];
		}, [savedSearchOverrides, listConfig, configuration]);

		const resolvedFilterRules = useMemo<Record<string, FilterRule>>(() => {
			if (savedSearchOverrides?.filterRules != null) {
				return savedSearchOverrides.filterRules;
			}
			return listConfig.filterRules ?? {};
		}, [savedSearchOverrides, listConfig]);

		const resolvedListOverrides = useMemo<Record<string, unknown>>(() => {
			if (savedSearchOverrides?.listOverrides != null) {
				return savedSearchOverrides.listOverrides;
			}
			return {};
		}, [savedSearchOverrides]);

		// The baseline a list inherits when it has NO saved-search override — i.e.
		// the bundled / base-list config. This is what storing `null` (in
		// getPendingChanges below) actually means. The "store null vs. store an
		// explicit override" decision MUST compare the working set against this
		// baseline, NOT against the override-inclusive `resolved*` values above:
		// `resolved*` is what the editor was seeded with (the previously-saved
		// override), so comparing to it makes "unchanged since last save" look like
		// "matches the inherited baseline" and writes `null`, wiping the override on
		// the next save (PD-13547: editing a filter's label dropped the filter
		// entirely; re-adding it then wiped the label).
		const inheritedActiveFilters = useMemo<Array<string>>(() => {
			if (listConfig.activeFilters != null) {
				return listConfig.activeFilters;
			}
			return configuration?.filters.activeFilters ?? [];
		}, [listConfig, configuration]);

		const inheritedFilterRules = useMemo<Record<string, FilterRule>>(
			() => listConfig.filterRules ?? {},
			[listConfig],
		);

		// Working state — initialized from resolved, mutated by user.
		const [activeFilters, setActiveFilters] = useState<Array<string>>(
			resolvedActiveFilters,
		);
		const [filterRules, setFilterRules] =
			useState<Record<string, FilterRule>>(resolvedFilterRules);
		const [listOverrides, setListOverrides] = useState<Record<string, unknown>>(
			resolvedListOverrides,
		);
		const [isDirty, setIsDirty] = useState(false);

		// Re-seed working state only when the user actually selects a different
		// list in the sidebar. listConfig.id is the per-list identifier (saved
		// search id or bundled list id). Without this guard we'd reset every
		// parent render because workingListConfig is rebuilt as a new object
		// each render and resolvedActiveFilters etc. lose reference equality —
		// wiping any in-progress edits the user made.
		const previousListIdRef = useRef<string | null>(null);
		useEffect(() => {
			const currentId = listConfig.id;
			if (previousListIdRef.current === currentId) {
				return;
			}
			previousListIdRef.current = currentId;
			setActiveFilters(resolvedActiveFilters);
			setFilterRules(resolvedFilterRules);
			setListOverrides(resolvedListOverrides);
			setIsDirty(false);
		}, [
			listConfig.id,
			resolvedActiveFilters,
			resolvedFilterRules,
			resolvedListOverrides,
		]);

		useEffect(() => {
			onStateChange({ hasPendingChanges: isDirty });
		}, [isDirty, onStateChange]);

		// Custom-field filters: fetched per object table in this list. Synthesized
		// server-side from CustomFieldDefinitionEntity rows so any tenant-defined
		// custom field (e.g. Pharmsource's sales_channel on order) shows up as an
		// available filter automatically — no code change per filter.
		const objectTables = useMemo(
			() => (configuration?.objects ?? []).map((o) => o.objectTable),
			[configuration?.objects],
		);

		const [customFieldConfigs, setCustomFieldConfigs] = useState<
			Record<string, FilterConfig>
		>({});

		useEffect(() => {
			let cancelled = false;
			void (async (): Promise<void> => {
				const merged: Record<string, FilterConfig> = {};
				for (const objectTable of objectTables) {
					const result = await CustomFieldQueryService.getFilterConfigs(
						objectTable as CustomFieldEntityType,
					);
					if (result.success) {
						Object.assign(merged, result.data);
					}
				}
				if (!cancelled) {
					setCustomFieldConfigs(merged);
				}
			})();
			return () => {
				cancelled = true;
			};
		}, [objectTables.join(',')]);

		// Available filters: anything in FILTER_REGISTRY valid for this list's
		// objects (and this listKey — filters with a listKeys restriction only
		// show on their declared lists) PLUS synthesized custom-field configs,
		// minus what's already active. Filters not in either source still
		// render in the active list (stale ids stay visible so the admin can
		// remove them).
		const listBuilderContext = useListBuilderContextSafe();
		const validFilterIds = useMemo(
			() =>
				getValidFiltersForObjects(
					configuration?.objects ?? [],
					listBuilderContext?.listKey,
				),
			[configuration?.objects, listBuilderContext?.listKey],
		);

		const availableFilters = useMemo(() => {
			const active = new Set(activeFilters);
			const fromRegistry = validFilterIds
				.filter((id) => !active.has(id))
				.map((id) => {
					const cfg = FilterRegistry.getConfig(id);
					return {
						id,
						label: cfg?.label ?? id,
						type: cfg?.type ?? 'unknown',
					};
				});
			const fromCustomFields = Object.entries(customFieldConfigs)
				.filter(([id]) => !active.has(id) && !validFilterIds.includes(id))
				.map(([id, cfg]) => ({
					id,
					label: cfg.label,
					type: cfg.type,
				}));
			return [...fromRegistry, ...fromCustomFields].sort((a, b) =>
				a.label.localeCompare(b.label),
			);
		}, [validFilterIds, activeFilters, customFieldConfigs]);

		const reorderActiveFilters = (newOrder: Array<string>): void => {
			if (isReadOnly) {
				return;
			}
			setActiveFilters(newOrder);
			markDirty();
		};

		const markDirty = (): void => {
			setIsDirty(true);
		};

		/**
		 * Insert a filter at a specific index in the active list. Called by
		 * the dual-list drag-and-drop when an available filter is dropped.
		 * Idempotent — if the filter is already active and the user re-drops
		 * it to a different slot, we move it.
		 */
		const addFilterAtPosition = (
			filterId: string,
			targetIndex: number,
		): void => {
			if (isReadOnly) {
				return;
			}
			setActiveFilters((prev) => {
				const without = prev.filter((id) => id !== filterId);
				const clampedIndex = Math.max(0, Math.min(targetIndex, without.length));
				const next = [...without];
				next.splice(clampedIndex, 0, filterId);
				return next;
			});
			markDirty();
		};

		const removeFilter = (filterId: string): void => {
			if (isReadOnly) {
				return;
			}
			setActiveFilters((prev) => prev.filter((id) => id !== filterId));
			// Also drop the rule for the removed filter so we don't carry stale config
			setFilterRules((prev) => {
				if (!(filterId in prev)) {
					return prev;
				}
				const next = { ...prev };
				delete next[filterId];
				return next;
			});
			markDirty();
		};

		const updateRule = (
			filterId: string,
			rule: FilterRule | undefined,
		): void => {
			if (isReadOnly) {
				return;
			}
			setFilterRules((prev) => {
				const next = { ...prev };
				if (rule == null) {
					delete next[filterId];
				} else {
					next[filterId] = rule;
				}
				return next;
			});
			markDirty();
		};

		const setShowFiltersByDefault = (value: boolean): void => {
			if (isReadOnly) {
				return;
			}
			setListOverrides((prev) => {
				const next = { ...prev, showFiltersByDefault: value };
				return next;
			});
			markDirty();
		};

		useImperativeHandle(ref, () => ({
			getPendingChanges: () => {
				if (!isDirty) {
					return null;
				}

				// Persist activeFilters as override when the user has touched anything.
				// Use null only when the working set matches the INHERITED baseline
				// exactly (the bundled / base-list config — what null inherits), so a
				// genuine revert keeps storage clean. Comparing against the inherited
				// baseline rather than the override-inclusive resolved values is what
				// stops a save from wiping the list's own override (PD-13547).
				const inheritedFiltersMatch =
					activeFilters.length === inheritedActiveFilters.length &&
					activeFilters.every((id, i) => id === inheritedActiveFilters[i]);
				const inheritedRulesMatch =
					JSON.stringify(filterRules) === JSON.stringify(inheritedFilterRules);
				const inheritedOverridesMatch =
					JSON.stringify(listOverrides) ===
					JSON.stringify(resolvedListOverrides);

				return {
					activeFilters: inheritedFiltersMatch ? null : activeFilters,
					filterRules:
						inheritedRulesMatch || Object.keys(filterRules).length === 0
							? null
							: filterRules,
					listOverrides:
						inheritedOverridesMatch || Object.keys(listOverrides).length === 0
							? null
							: listOverrides,
				};
			},
			reset: () => {
				setActiveFilters(resolvedActiveFilters);
				setFilterRules(resolvedFilterRules);
				setListOverrides(resolvedListOverrides);
				setIsDirty(false);
			},
		}));

		return (
			<div className="flex-1 overflow-y-auto p-4 space-y-4">
				{isReadOnly && (
					<div
						className={cn(
							'px-3 py-2 rounded border text-sm',
							colors.classes.borderDefault,
							colors.classes.calloutWarning,
						)}
					>
						{t(
							'This is a read-only list. Use "Copy List" in the footer to create an editable copy.',
						)}
					</div>
				)}

				{/* List behavior — top of the tab so the global on/off toggle
				    is the first thing the admin sees. */}
				<section
					className={cn(
						'rounded-md border p-3',
						colors.classes.borderDefault,
						colors.classes.bgPrimary,
					)}
				>
					<header className="mb-2">
						<h3
							className={cn(
								'text-sm font-semibold',
								colors.classes.textPrimary,
							)}
						>
							{t('List behavior')}
						</h3>
					</header>
					<Checkbox
						checked={listOverrides['showFiltersByDefault'] !== false}
						onChange={(checked) => {
							setShowFiltersByDefault(checked);
						}}
						disabled={isReadOnly}
						label={t('Show filter bar expanded by default')}
						help={t(
							'When checked, the filter sidebar appears expanded the first time a user opens this list. They can still collapse it manually.',
						)}
					/>
				</section>

				<DualSortableProvider
					selected={activeFilters}
					keyExtractor={(filterId) => filterId}
					availableIds={availableFilters.map((f) => f.id)}
					onReorder={reorderActiveFilters}
					onAdd={(filterId, index) => {
						addFilterAtPosition(filterId, index);
					}}
					onRemove={(filterId) => {
						removeFilter(filterId);
					}}
					disabled={isReadOnly}
					renderOverlay={(id) => {
						const label =
							FilterRegistry.getConfig(id)?.label ??
							customFieldConfigs[id]?.label ??
							id;
						return (
							<div
								className={cn(
									'flex items-center gap-2 px-2 py-1.5 rounded border shadow-lg',
									colors.classes.borderDefault,
									colors.classes.bgPrimary,
								)}
							>
								<Icon
									icon={faGripVertical}
									size="xs"
									className={cn('shrink-0', colors.classes.textMuted)}
								/>
								<span className={cn('text-sm', colors.classes.textPrimary)}>
									{t(label)}
								</span>
							</div>
						);
					}}
				>
					{/* Active filters — single consolidated list. Each entry is an
					    expandable FilterRuleCard with a drag handle in its header.
					    Reorderable; drag a card into the available pool to remove it. */}
					<section
						className={cn(
							'rounded-md border p-3',
							colors.classes.borderDefault,
							colors.classes.bgPrimary,
						)}
					>
						<header className="flex items-center justify-between mb-2">
							<h3
								className={cn(
									'text-sm font-semibold',
									colors.classes.textPrimary,
								)}
							>
								{t(`Active filters (${String(activeFilters.length)})`)}
							</h3>
							<p className={cn('text-xs', colors.classes.textMuted)}>
								{t(
									'Drag the handle to reorder. Drag a filter from the available pool to add it, or drag a card there to remove it.',
								)}
							</p>
						</header>

						<SortableZone
							container="selected"
							items={activeFilters}
							keyExtractor={(filterId) => filterId}
							className="space-y-2 min-h-[2.5rem]"
							emptyContent={
								<div
									className={cn(
										'rounded border-2 border-dashed py-6 text-center text-xs',
										colors.classes.borderDefault,
										colors.classes.textMuted,
									)}
								>
									{t('Drag a filter here from the available pool below.')}
								</div>
							}
							renderItem={(filterId, isDraggingItem) => (
								<FilterRuleCard
									filterId={filterId}
									rule={filterRules[filterId]}
									customFieldConfig={customFieldConfigs[filterId]}
									onChange={(next) => {
										updateRule(filterId, next);
									}}
									onRemove={() => {
										removeFilter(filterId);
									}}
									disabled={isReadOnly}
									showDragHandle={!isReadOnly}
									isDragging={isDraggingItem}
								/>
							)}
						/>
					</section>

					{/* Available filters — drag into Active to add; drag a card here to remove */}
					<section
						className={cn(
							'rounded-md border p-3',
							colors.classes.borderDefault,
							colors.classes.bgPrimary,
						)}
					>
						<header className="mb-2">
							<h3
								className={cn(
									'text-sm font-semibold',
									colors.classes.textPrimary,
								)}
							>
								{t(`Available filters (${String(availableFilters.length)})`)}
							</h3>
							<p className={cn('text-xs', colors.classes.textMuted)}>
								{t(
									'Drag a filter into the Active list to add it — or drag an active card here to remove it.',
								)}
							</p>
						</header>

						<SortableZone
							container="available"
							items={availableFilters}
							keyExtractor={(f) => f.id}
							className="space-y-1 max-h-72 overflow-y-auto min-h-[2.5rem]"
							emptyContent={
								<div
									className={cn(
										'rounded border-2 border-dashed py-6 text-center text-xs',
										colors.classes.borderDefault,
										colors.classes.textMuted,
									)}
								>
									{t('Drag an active filter here to remove it.')}
								</div>
							}
							renderItem={(f) => (
								<AvailableFilterItem filter={f} disabled={isReadOnly} />
							)}
						/>
					</section>
				</DualSortableProvider>
			</div>
		);
	},
);

FiltersTabContent.displayName = 'FiltersTabContent';

interface AvailableFilterItemProps {
	filter: { id: string; label: string; type: string };
	disabled: boolean;
}

/**
 * One row in the Available filters list. Drag is handled by the shared
 * DualSortableProvider (dnd-kit) via the DragHandle below.
 */
function AvailableFilterItem({
	filter,
	disabled,
}: AvailableFilterItemProps): React.ReactElement {
	const { t } = useTranslation();
	const colors = useThemeColors();
	const isCustomField = filter.id.startsWith('cf:');

	return (
		<div
			className={cn(
				'flex items-center gap-2 px-2 py-1.5 rounded border select-none',
				colors.classes.borderDefault,
				colors.classes.bgPrimary,
				!disabled && `hover:${colors.classes.bgSecondary}`,
				disabled && 'opacity-50',
			)}
		>
			{!disabled && <DragHandle className="shrink-0" />}
			<span
				className={cn('text-sm flex-1 truncate', colors.classes.textPrimary)}
			>
				{t(filter.label)}
			</span>
			{isCustomField && (
				<span
					className={cn(
						'text-xs px-1.5 py-0.5 rounded shrink-0',
						'bg-chip-4 text-chip-4 border border-chip-4',
					)}
				>
					{t('custom')}
				</span>
			)}
			<span
				className={cn(
					'text-xs px-1.5 py-0.5 rounded shrink-0',
					colors.classes.bgSecondary,
					colors.classes.textSecondary,
				)}
			>
				{t(filter.type)}
			</span>
		</div>
	);
}
